// Thẻ "Duyệt số mặt" — soát lời khai số mặt trang điểm của từng buổi.
//
// Tệp riêng chứ không nhét vào `tabs-dieu-hanh.ts`: ba thẻ bên đó nói về việc
// XẾP và THEO DÕI ca, còn thẻ này soát một con số của buổi chụp. Người sửa bài
// này không phải cuộn qua bài xếp lịch tuần.
//
// ⚠️ Ranh giới thông tin ở `../../types.ts` áp NGUYÊN VẸN — bài này KHÔNG nói
// con số ấy nuôi khoản nào, cũng không nói nhóm nghề nào mở được thẻ. Thẻ chỉ
// hiện với người mở được nó, nên viết ra là dạy người khác biết nhắm vào đâu.

import { BRANCH_SHIFT_TAB_LABEL } from "@/features/branch-shift/labels";
import type { GuideTab } from "../../types";

export const TAB_DUYET_SO_MAT: GuideTab = {
  key: "makeupFaces",
  label: BRANCH_SHIFT_TAB_LABEL.makeupFaces,
  purpose: "Soát lại số mặt trang điểm mà ê-kíp đã khai cho từng buổi, rồi duyệt hoặc trả lại.",
  where: [
    'Ba thẻ nhỏ ở đầu: "Chờ duyệt", "Đã duyệt", "Đã trả lại". Mở ra là đứng ở "Chờ duyệt".',
    "Mỗi thẻ việc là MỘT buổi chụp: mã đơn, cơ sở, gói và ngày chụp ở góc trái.",
    "Góc phải mỗi thẻ việc: tổng số mặt cả ê-kíp đã khai, bên dưới là số người được trang điểm ghi trên đơn và dòng chia ra mấy nữ · mấy nam · mấy bé.",
    "Giữa thẻ việc: danh sách từng người trong nhóm trang điểm của buổi, kèm con số của họ; ai được chia theo loại thì trong ngoặc ghi mấy nữ · mấy nam · mấy bé.",
    'Cuối thẻ việc: nút "Duyệt cả buổi" và nút "Trả lại".',
  ],
  howItWorks: [
    "Người trong nhóm trang điểm của một buổi tự điền con số của chính mình; mỗi người chỉ điền được ô của mình, không đè lên ô người khác.",
    "Quầy xếp người cho buổi cũng chia sẵn được mỗi người mấy nữ, mấy nam, mấy bé; phần chia ấy cũng vào thẻ Chờ duyệt như một lời khai.",
    "Buổi nào có người tự điền hoặc được chia sẵn thì nằm ở thẻ Chờ duyệt cho tới khi bạn soát xong.",
    "Duyệt là duyệt CẢ BUỔI một lần — không duyệt lẻ từng người.",
    "Buổi chưa được soát vẫn chạy bình thường, chỉ là con số vừa khai chưa dùng tới.",
    "Ai đó sửa lại con số sau khi bạn đã duyệt thì buổi ấy quay về thẻ Chờ duyệt, phải soát lại.",
    'Trả lại không xoá con số cũ: nó vẫn nằm đó để ê-kíp biết sửa chỗ nào, và buổi chuyển sang thẻ "Đã trả lại".',
  ],
  statuses: [
    { label: "Chờ duyệt", meaning: "Có người trong ê-kíp vừa tự điền con số, chưa ai soát." },
    { label: "Đã duyệt", meaning: "Bạn đã soát và chấp nhận con số của cả buổi." },
    { label: "Đã trả lại", meaning: "Bạn đã trả về cho ê-kíp kèm lý do; chờ họ sửa rồi điền lại." },
  ],
  blocks: [
    {
      for: ["lead", "exec"],
      title: "Soát một buổi",
      steps: [
        "Mở thẻ, đứng ở Chờ duyệt — danh sách đã giới hạn trong các cơ sở bạn phụ trách.",
        "Với từng buổi: so con số ở góc phải — tổng cả ê-kíp khai, với số người được trang điểm ghi trên đơn.",
        "Nhìn danh sách từng người: buổi có nhiều thợ thì con số phải phản ánh ai làm nhiều, ai làm ít — và phần nữ / nam / bé của cả nhóm cộng lại phải khớp dòng chia của đơn.",
        'Thấy hợp lý thì bấm "Duyệt cả buổi".',
        'Thấy sai thì bấm "Trả lại", gõ một câu nói rõ sai chỗ nào, rồi bấm "Gửi lý do".',
        "Làm hết thẻ Chờ duyệt trước khi cơ sở chốt tháng — việc tồn ở đây thì con số của tháng chưa xong.",
      ],
      troubles: [
        'Tổng hiện màu đỏ và có dòng cảnh báo: cả ê-kíp khai nhiều hơn số người được trang điểm ghi trên đơn. Bấm Duyệt sẽ bị từ chối — trả lại cho ê-kíp, hoặc nhờ quầy sửa ô "số người trang điểm" nếu khách mua thêm tại chỗ.',
        'Dòng chữ "đơn chưa khai số người trang điểm": đơn đó bỏ trống ô ấy nên không có gì để đối chiếu. Nhờ quầy điền trước rồi hãy soát.',
        "Bấm Duyệt mà máy báo từ chối: đọc nguyên văn câu báo — nó nói đúng cái đang vướng, thường là tháng đã chốt hoặc con số vừa đổi.",
        "Một buổi không hiện dù có người báo đã điền: buổi đó ở cơ sở ngoài phạm vi bạn phụ trách, hoặc chưa ai bấm lưu.",
        'Nút "Trả lại" bấm vào không gửi được: phải gõ lý do trước — bỏ trống thì nút gửi vẫn mờ.',
      ],
    },
  ],
};
